import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { extApi } from '../api/extensibilityClient';
import type { Variable } from '../types';

export function LinkageBuilderPage() {
  const [variables, setVariables] = useState<Variable[]>([]);
  const [graph, setGraph] = useState<any>(null);
  const [form, setForm] = useState({
    source: '', target: '', relationship_type: 'linear', formula: 'output = shock * coefficient * weight * phase_in * sign',
    coefficient: '1', in_gdp: true,
  });
  const [validation, setValidation] = useState<string[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.gdpDrivers().then(setVariables).catch(() => extApi.variables().then((v) => setVariables(v as Variable[])));
    extApi.dependencyGraph().then(setGraph).catch(console.error);
  }, []);

  const validate = async () => {
    const r = await extApi.validateFormula(form.formula) as { valid: boolean; errors: string[] };
    setValidation(r.errors || []);
  };

  const save = async () => {
    await extApi.createLinkage({
      source_variable_id: form.source,
      target_variable_id: form.target || null,
      relationship_type: form.relationship_type,
      formula_expression: form.formula,
      coefficient: parseFloat(form.coefficient),
      in_gdp_transmission: form.in_gdp && !form.target,
    });
    setMsg('Linkage saved');
    extApi.dependencyGraph().then(setGraph);
  };

  return (
    <div>
      <h1 className="page-title">Linkage Builder</h1>
      <p className="page-subtitle">Construct Variable A → Variable B → Output chains with safe formula validation.</p>

      <div className="card">
        <h3>Define Linkage</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <label style={{ fontSize: '0.85rem' }}>Source variable
            <select className="field" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <option value="">Select…</option>
              {variables.map((v) => <option key={v.id} value={v.id}>{v.id}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.85rem' }}>Target variable (blank = GDP)
            <select className="field" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })}>
              <option value="">GDP (transmission)</option>
              {variables.map((v) => <option key={v.id} value={v.id}>{v.id}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.85rem' }}>Relationship type
            <select className="field" value={form.relationship_type} onChange={(e) => setForm({ ...form, relationship_type: e.target.value })}>
              {['linear', 'elasticity', 'multiplier', 'percentage_point', 'threshold', 'piecewise', 'qualitative_score', 'identity', 'custom'].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: '0.85rem' }}>Coefficient
            <input className="field" value={form.coefficient} onChange={(e) => setForm({ ...form, coefficient: e.target.value })} />
          </label>
        </div>
        <label style={{ fontSize: '0.85rem', display: 'block', marginTop: '0.75rem' }}>Formula
          <textarea className="field" rows={3} style={{ width: '100%' }} value={form.formula} onChange={(e) => setForm({ ...form, formula: e.target.value })} />
        </label>
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
          <button className="btn secondary" type="button" onClick={validate}>Validate formula</button>
          <button className="btn" type="button" onClick={save}>Save linkage</button>
        </div>
        {validation.length > 0 && <ul style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{validation.map((e) => <li key={e}>{e}</li>)}</ul>}
        {msg && <p style={{ color: '#16a34a' }}>{msg}</p>}
      </div>

      {graph && (
        <div className="card">
          <h3>Linkage Graph ({graph.nodes.length} nodes, {graph.edges.length} edges)</h3>
          {graph.cycles?.length > 0 && <p style={{ color: '#b91c1c' }}>Cycle detected: {graph.cycles[0].join(' → ')}</p>}
          <div className="graph-preview">
            {graph.edges.slice(0, 40).map((e: any, i: number) => (
              <div key={i} className="graph-edge">{e.source} → {e.target} <span style={{ color: '#6b7280' }}>({e.type})</span></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
